// src/pages/admin/AdminSettingsPage.tsx
// Admin hub — Overview (stats + shortcut to /audit), Settings (maintenance,
// notifications, federation), Audit Log (AdminService's own unified log).
// Entirely admin-gated (AdminRoute, in App.tsx). See docs/ux/admin-ia.md.

import { useState } from 'react';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { OverviewTab } from '@/features/admin/OverviewTab';
import { SettingsTab } from '@/features/admin/SettingsTab';
import { AuditLogTab } from '@/features/admin/AuditLogTab';

type Tab = 'overview' | 'settings' | 'auditLog';

export const AdminSettingsPage = observer(() => {
  const { t } = useI18n();
  const [tab, setTab] = useState<Tab>('overview');

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: t('admin.tabs.overview') },
    { id: 'settings', label: t('admin.tabs.settings') },
    { id: 'auditLog', label: t('admin.tabs.auditLog') },
  ];

  return (
    <div className="flex h-full flex-col space-y-6 p-6">
      {/* The tab already names this page; the heading stays for screen readers. */}
      <h1 className="sr-only">{t('admin.page.title')}</h1>

      <div className="flex items-center gap-1 border-b border-border" role="tablist">
        {tabs.map((tb) => (
          <button
            key={tb.id}
            type="button"
            role="tab"
            aria-selected={tab === tb.id}
            onClick={() => setTab(tb.id)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
              tab === tb.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tb.label}
          </button>
        ))}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {tab === 'overview' && <OverviewTab />}
        {tab === 'settings' && <SettingsTab />}
        {tab === 'auditLog' && <AuditLogTab />}
      </div>
    </div>
  );
});

export default AdminSettingsPage;
